import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Mixin from 'components/common/Mixin'
import Link from 'components/common/Link/Link'
import Icon from 'components/common/Icon/Icon'
import Media from 'components/common/Media'

import toggle from 'components/common/hoc/toggle'

// TODO generalize desktop and mobile styles

const MenuItemWithToggle = toggle(
  ({
    mobileMainMenuExpanded,
    mobileMainMenuToggleHandlers,
    expanded,
    toggleHandlers,
    links,
    name,
    contentSpotlights
  }) =>
    <MenuItem hidden={ mobileMainMenuExpanded && !expanded } expanded={ expanded }>
      <MenuTitle
        expanded={ expanded }
        onClick={
          () => {
            toggleHandlers.onClick()
            mobileMainMenuToggleHandlers.onClick()
          }
        }
        onKeyPress={
          () => {
            toggleHandlers.onKeyPress()
            mobileMainMenuToggleHandlers.onKeyPress()
          }
        }>
        <Back hidden={ !expanded } >
          <BackIcon asset="arrow-left" alt="" height={ 12 } />Back
        </Back>

        { name }

        <FollowIcon asset="arrow-right" alt="" width={ 10 } hidden={ expanded } />
      </MenuTitle>
      <SubMenu hidden={ !expanded } >
        { React.Children.map(links, link => <SubMenuItem>{ link }</SubMenuItem>) }
      </SubMenu>
    </MenuItem>
)

const MenuLinkWithContentSpotlight = ({
  mobileMainMenuExpanded,
  mobileMainMenuToggleHandlers,
  name,
  link: { path = '/' } = {},
  placeholders: { links = [], contentSpotlights = []} = {}, ...props
}) => {
  const isSubmenu = Boolean(React.Children.count(links))

  return (
    <Wrapper>
      <Media.Desktop>
        <NavItem className="nav-item" submenu={ isSubmenu }>
          <NavLink to={ path } submenu={ isSubmenu }>
            { name }
          </NavLink>
          <SubNav>
            <SubNavLinks>
              { links }
            </SubNavLinks>
          </SubNav>
        </NavItem>
      </Media.Desktop>

      <Media.Mobile>
        <MenuItemWithToggle
          mobileMainMenuExpanded={ mobileMainMenuExpanded }
          mobileMainMenuToggleHandlers={ mobileMainMenuToggleHandlers }
          links={ links }
          name={ name }
          contentSpotlights={ contentSpotlights } />
      </Media.Mobile>
    </Wrapper>
  )
}

MenuLinkWithContentSpotlight.propTypes = {
  '@type': PropTypes.string,
  displayName: PropTypes.string,
  'endeca:auditInfo': PropTypes.shape({
    'ecr:innerPath': PropTypes.string,
    'ecr:resourcePath': PropTypes.string
  }),
  link: PropTypes.shape({
    '@class': PropTypes.string,
    linkType: PropTypes.string,
    path: PropTypes.string,
    queryString: PropTypes.string
  }),
  mobileMainMenuExpanded: PropTypes.bool,
  mobileMainMenuToggleHandlers: PropTypes.shape({
    onClick: PropTypes.func,
    onKeyPress: PropTypes.func
  }),
  name: PropTypes.string,
  placeholders: PropTypes.shape({
    links: PropTypes.arrayOf(PropTypes.shape()),
    contentSpotlights: PropTypes.arrayOf(PropTypes.shape())
  })
}

MenuLinkWithContentSpotlight.type = 'MenuLinkWithContentSpotlight'
MenuLinkWithContentSpotlight.placeholders = ['links', 'contentSpotlights']

export default MenuLinkWithContentSpotlight

const Wrapper = styled.div`
  display: inline-block;
  ${Media.mobile`
    display: block;
  `}
`

const NavItem = styled.div`
  position: relative;
  z-index: 2;
  display: inline-block;
  margin-right: 8px;
  ${props => props.submenu && css`
    :hover {
      background: ${Theme.colors.white};
      box-shadow: 0 2px 8px ${Theme.colors.transparent_grey_hover};

      > div {
        display: block;
      }

      > a {
        background: ${Theme.colors.white};
      }
    }
  `}
`

const NavLink = styled(Link)`
  position: relative;
  display: block;
  padding: 12px 18px 8px;
  color: ${Theme.colors.black};
  ${Mixin.font.GothamBold(16, 600)}
  text-transform: uppercase;

  :hover {
    text-decoration: none;
  }

  ${props => props.submenu && css`
    position: relative;
    z-index: 2;
  `}
`

const FollowIcon = styled(Icon)`
  position: absolute;
  right: 22px;
  margin-top: 5px;
`

const SubNav = styled.div`
  position: absolute;
  top: 100%;
  left: 1px;
  z-index: 1;
  display: none;
  width: auto;
  padding: 0;
  padding-left: 20px;
  margin-top: -1px;
  margin-left: -1px;
  white-space: nowrap;
  background: ${Theme.colors.white};
  box-shadow: 0 2px 8px ${Theme.colors.transparent_grey_hover};
`

const SubNavLinks = styled.div`
  display: table-cell;
  min-width: 160px;
  padding-top: 11px;
  font-size: 14px;
  font-weight: 400;
  line-height: 36px;
  text-align: left;
  vertical-align: top;

  & ${Link} {
    display: block;
    color: ${Theme.colors.sub_grey};
    border-top: 1px solid ${Theme.colors.light_grey};

    :first-child {
      border-top: none;
    }

    :hover {
      text-decoration: none;
    }
  }
`


const MenuItem = styled.div`
  position: relative;
  padding: 20px 0;
  margin-left: 16px;
  text-transform: uppercase;
  border-bottom: 1px solid ${Theme.colors.light_grey};
  ${props => props.expanded && css`
    border-bottom: 0;
  `}
  ${props => props.alternative && css`
    background: ${Theme.colors.gray};
  `}
`

const SubMenu = styled.div`
  font-weight: normal;
`

const MenuTitle = styled.div`
  cursor: pointer;
  ${Mixin.font.GothamBold(16, 600)}
  ${props => props.expanded && css`
    padding-bottom: 22px;
    text-align: center;
    border-bottom: 1px solid  ${Theme.colors.gray};
  `}
`

const Back = styled.div`
  position: absolute;
  top: 2px;
  left: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${Theme.colors.lightblue};
`

const BackIcon = styled(Icon)`
  margin: -2px 8px 0 0;
`

const SubMenuItem = styled.div`
  padding: 18px 0 21px;
  line-height: 21px;
  border-bottom: 1px solid  ${Theme.colors.gray};

  & ${Link} {
    font-size: 16px;
    font-weight: 500;
    color: ${Theme.colors.black};
  }
`
